import React, { useState, useEffect, useRef } from 'react';
import MMEmailSetupBanner from './MMEmailSetupBanner';
import AssignButton from './AssignButton';
import DictateButton from './DictateButton';
import { useShortcuts } from './useShortcuts';
import ShortcutSuggestions from './ShortcutSuggestions';
import LiveAlertBar from './LiveAlertBar';
import axios from 'axios';

const API = '/api';

interface EmailThread {
  id: string;
  subject: string;
  sender_email: string;
  sender_name: string;
  account_id: string;
  account_name: string;
  last_message_at: string;
  is_read: boolean;
  is_handled: boolean;
  message_count: number;
  has_attachments?: boolean;
  assigned_to?: string | null;
  language?: string | null;
}

interface EmailAttachment {
  id: string;
  original_filename: string;
  mime_type: string;
  size_bytes: number;
}

interface EmailMessage {
  id: string;
  body_text_pl?: string | null;
  sender_email: string;
  sender_name: string;
  body_text: string;
  body_html?: string | null;
  is_outgoing: boolean;
  received_at: string;
  has_attachments?: boolean;
  spf_pass?: boolean | null;
  dkim_pass?: boolean | null;
  attachments?: EmailAttachment[];
}

function formatDate(d: string): string {
  if (!d) return '';
  const s = d.includes('Z') || d.includes('+') ? d : d + 'Z';
  const date = new Date(s);
  const diff = new Date().getTime() - date.getTime();
  if (diff < 86400000) return date.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
  return date.toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

function EmailFrame({ html }: { html: string }) {
  const iframeRef = React.useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = React.useState(300);
  const css = 'body{margin:0;padding:8px;font-family:system-ui,sans-serif;font-size:13px;color:#374151}img{max-width:100%!important;height:auto!important}a{color:#1d4ed8}';
  const srcDoc = '<!DOCTYPE html><html><head><meta charset="utf-8"><style>' + css + '</style></head><body>' + html + '</body></html>';
  const onLoad = () => { try { const h = iframeRef.current?.contentDocument?.body?.scrollHeight; if (h) setHeight(h + 20); } catch(e) {} };
  return <iframe ref={iframeRef} srcDoc={srcDoc} onLoad={onLoad} style={{ width: '100%', height: height, border: 'none', display: 'block' }} sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation" title="email" />;
}

function activateImages(html: string): string {
  return html.replace(/data-src=/g, 'src=').replace(/style="display:none;/g, 'style="display:inline;');
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' KB';
  return (bytes / 1024 / 1024).toFixed(1) + ' MB';
}

function getFileIcon(mime: string): string {
  if (!mime) return '📎';
  if (mime.startsWith('image/')) return '🖼';
  if (mime === 'application/pdf') return '📄';
  if (mime.includes('word') || mime.includes('odt')) return '📝';
  if (mime.includes('excel') || mime.includes('ods')) return '📊';
  if (mime.startsWith('text/')) return '📃';
  return '📎';
}

function formatDateTime(d: string): string {
  if (!d) return '';
  const s = d.includes('Z') || d.includes('+') ? d : d + 'Z';
  return new Date(s).toLocaleString('pl-PL', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
}

export default function EmailInbox({ onBack, hideHeader, hideAlertBar, mobile, currentUser = null, onOpenAiOpen = () => {}, onOpenReturns = () => {} }: {
  onBack: () => void;
  hideHeader?: boolean;
  hideAlertBar?: boolean;
  mobile?: boolean;
  currentUser?: any;
  onOpenAiOpen?: () => void;
  onOpenReturns?: () => void;
}) {
  const [threads, setThreads] = useState<EmailThread[]>([]);
  const [copyToast, setCopyToast] = useState('');
  const [selected, setSelected] = useState<EmailThread | null>(null);
  const [messages, setMessages] = useState<EmailMessage[]>([]);
  const [reply, setReply] = useState('');
  const [replyToAddress, setReplyToAddress] = useState('');
  const replyTextareaRef = useRef<HTMLTextAreaElement>(null);
  const { showSuggestions: showReplySuggestions, suggestions: replySuggestions, handleChange: handleReplyChange, applyShortcut: applyReplyShortcut, setShowSuggestions: setShowReplySuggestions } = useShortcuts();
  const { showSuggestions: showComposeSuggestions, suggestions: composeSuggestions, handleChange: handleComposeChange, applyShortcut: applyComposeShortcut, setShowSuggestions: setShowComposeSuggestions } = useShortcuts();
  const [filter, setFilter] = useState('unread');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [showImages, setShowImages] = useState<Set<string>>(new Set());
  const [selectedThreadIds, setSelectedThreadIds] = useState<Set<string>>(new Set());
  const [ruleModal, setRuleModal] = useState<{sender:string, subject:string, account_id:string} | null>(null);
  const [ruleForm, setRuleForm] = useState({name:'', sender:'', subject:'', logic:'OR', account_id:'', assigned_to:''});
  const [ruleSaving, setRuleSaving] = useState(false);
  const [agents, setAgents] = useState<{id:string,name:string,color?:string}[]>([]);
  const [imapAccounts, setImapAccounts] = useState<{id:string,name:string}[]>([]);
  const [emailSetupClosed, setEmailSetupClosed] = useState(false);
  const [imapLoaded, setImapLoaded] = useState(false);
  const loadImapAccounts = () => {
    axios.get('/api/settings/imap-accounts').then(r => { setImapAccounts(r.data); setImapLoaded(true); }).catch(() => { setImapLoaded(true); });
  };
  const [aiLoading, setAiLoading] = useState(false);
  const [threadLang, setThreadLang] = useState('pl');
  const [replyTranslation, setReplyTranslation] = useState('');
  const [translatingReply, setTranslatingReply] = useState(false);
  const [replyIsManual, setReplyIsManual] = useState(false);
  const [translatingMsg, setTranslatingMsg] = useState<string|null>(null);
  const [showOriginalPl, setShowOriginalPl] = useState<{[id:string]:boolean}>({});
  const [replyAttachments, setReplyAttachments] = useState<File[]>([]);
  const [showCompose, setShowCompose] = useState(false);
  const [compose, setCompose] = useState({ to: '', subject: '', body: '', account_id: '', language: 'pl' });
  const [composeAttachments, setComposeAttachments] = useState<File[]>([]);
  const [composeSending, setComposeSending] = useState(false);
  const [composeAiLoading, setComposeAiLoading] = useState(false);
  const [composeTranslation, setComposeTranslation] = useState('');
  const [composeTranslating, setComposeTranslating] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [imageUrls, setImageUrls] = useState<Record<string, string>>({});
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  const loadHistory = async (threadId: string) => {
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${API}/email/threads/${threadId}/history`);
      setHistory(res.data || []);
    } catch(e) {}
    setHistoryLoading(false);
  };

  const downloadAttachment = async (attId: string, filename: string) => {
    try {
      const res = await axios.get(`${API}/email/attachments/${attId}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch { alert('Błąd pobierania załącznika'); }
  };

  const loadImageAttachment = async (attId: string) => {
    if (imageUrls[attId]) return;
    try {
      const res = await axios.get(`${API}/email/attachments/${attId}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      setImageUrls(prev => ({ ...prev, [attId]: url }));
    } catch { /* brak imap_uid lub błąd — zostaje przycisk */ }
  };

  const bulkHandle = async () => {
    if (selectedThreadIds.size === 0) return;
    await Promise.all(Array.from(selectedThreadIds).map(id =>
      axios.post(`${API}/email/threads/${id}/handle`)
    ));
    setSelectedThreadIds(new Set());
    fetchThreads();
  };

  const sendCompose = async () => {
    if (!compose.to || !compose.subject || !compose.body || !compose.account_id) return;
    setComposeSending(true);
    try {
      const fd = new FormData();
      fd.append('to', compose.to);
      fd.append('subject', compose.subject);
      fd.append('body', compose.body);
      fd.append('account_id', compose.account_id);
      if (compose.language !== 'pl') fd.append('language', compose.language);
      composeAttachments.forEach(f => fd.append('attachments', f));
      await axios.post(`${API}/email/compose`, fd);
      setShowCompose(false);
      setCompose({ to: '', subject: '', body: '', account_id: '', language: 'pl' });
      setComposeAttachments([]);
      setComposeTranslation('');
    } catch(e: any) {
      alert('Błąd wysyłania: ' + (e.response?.data?.detail || 'nieznany'));
    }
    setComposeSending(false);
  };

  const generateComposeAI = async () => {
    if (!compose.subject) return;
    setComposeAiLoading(true);
    try {
      const res = await axios.post(`${API}/ai/generate-reply`, {
        message: `Napisz email. Temat: ${compose.subject}`,
        history: [], account_id: compose.account_id || '', context: '', hint: compose.body || ''
      });
      setCompose(prev => ({...prev, body: res.data.draft || ''}));
    } catch(e) {}
    setComposeAiLoading(false);
  };

  const translateMessage = async (msgId: string, text: string) => {
    if (translations[msgId]) {
      setShowTranslation(prev => ({...prev, [msgId]: !prev[msgId]}));
      return;
    }
    setTranslatingMsg(msgId);
    try {
      const res = await axios.post('/api/ai/translate-to-polish', { text });
      setTranslations(prev => ({...prev, [msgId]: res.data.translation}));
      setShowTranslation(prev => ({...prev, [msgId]: true}));
    } catch(e) {}
    setTranslatingMsg(null);
  };
  const [translations, setTranslations] = useState<{[id:string]:string}>({});
  const [showTranslation, setShowTranslation] = useState<{[id:string]:boolean}>({});
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [mobileView, setMobileView] = useState<'list'|'thread'>('list');
  const PAGE_SIZE = 50;
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchThreads();
    axios.get('/api/agents').then(r => { setAgents(r.data); (window as any).__agents__ = r.data; }).catch(() => {});
    loadImapAccounts();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => { fetchThreads(0); }, 300);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const fetchThreads = async (p?: number, f?: string) => {
    const currentPage = p !== undefined ? p : page;
    const currentFilter = f !== undefined ? f : filter;
    setLoading(true);
    try {
      const params = new URLSearchParams({ offset: String(currentPage * PAGE_SIZE), limit: String(PAGE_SIZE) });
      if (currentFilter === 'unread') params.set('filter_unread', 'true');
      if (search.trim()) params.set('search', search.trim());
      const res = await axios.get(`${API}/email/threads?${params.toString()}`);
      setThreads(res.data.threads || []);
      setTotal(res.data.total || 0);
      if (p !== undefined) setPage(p);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const generateAI = async () => {
    if (!selected || messages.length === 0) return;
    const clientMsgs = messages.filter(m => !m.is_outgoing);
    if (!clientMsgs.length) return;
    const lastMsg = clientMsgs[clientMsgs.length-1].body_text || '';
    const history = messages.map(m => ({ text: m.body_text || '', is_client: !m.is_outgoing }));
    setAiLoading(true);
    try {
      const res = await axios.post(`${API}/ai/generate-reply`, { message: lastMsg, history, account_id: selected.account_id || '', context: '', hint: reply });
      setReply(res.data.draft || '');
      setReplyIsManual(false);
      setReplyTranslation('');
    } catch(e) {}
    setAiLoading(false);
  };

  const selectThread = async (t: EmailThread) => {
    setSelected(t);
    localStorage.setItem('assistant_context', 'email:thread');
    setThreadLang(t.language && t.language !== 'pl' ? t.language : 'pl');
    setReplyTranslation('');
    setTranslations({});
    setShowTranslation({});
    setShowOriginalPl({});
    setShowHistory(false);
    setHistory([]);
    setMessages([]);
    setReply('');
    setReplyAttachments([]);
    setReplyToAddress(t.sender_email || '');
    setSent(false);
    setLoadingMessages(true);
    if (mobile) setMobileView('thread');
    try {
      const res = await axios.get(`${API}/email/threads/${t.id}`);
      const fetchedMsgs = res.data.messages || [];
      setMessages(fetchedMsgs);
      // Jesli brak jezyka w wątku — wykryj z pierwszej wiadomosci klienta
      if (!t.language || t.language === 'pl') {
        const firstClientMsg = fetchedMsgs.find((m: any) => !m.is_outgoing);
        if (firstClientMsg?.body_text && firstClientMsg.body_text.length > 20) {
          try {
            const langRes = await axios.post(`${API}/ai/detect-language`, { text: firstClientMsg.body_text.slice(0, 500) });
            const detected = langRes.data?.language;
            if (detected && detected !== 'pl') setThreadLang(detected);
          } catch(e) {}
        }
      }
      if (!t.is_read) {
        setTimeout(() => {
          if (mobile && messagesContainerRef.current) {
            messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
          } else {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      }
    } catch (e) { console.error(e); }
    setLoadingMessages(false);
  };

  const sendReply = async (learn: boolean) => {
    if (!selected || !reply.trim()) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append('body', reply);
      if (threadLang !== 'pl') fd.append('language', threadLang);
      if (learn && replyIsManual) fd.append('body_pl', reply);
      fd.append('to_address', replyToAddress.trim());
      replyAttachments.forEach(f => fd.append('attachments', f));
      const res = await axios.post(`${API}/email/threads/${selected.id}/reply`, fd);
      if (res.data.status === 'ok') {
        setReply('');
        setReplyAttachments([]);
        setSent(true);
        setTimeout(() => setSent(false), 2000);
        selectThread(selected);
      } else {
        alert('Błąd wysyłki: ' + (res.data.message || 'nieznany'));
      }
    } catch (e: any) {
      alert('Błąd wysyłki: ' + (e.response?.data?.detail || 'nieznany'));
    }
    setSending(false);
  };

  const handleThread = async (t: EmailThread) => {
    await axios.post(`${API}/email/threads/${t.id}/handle`);
    setSelected(prev => prev ? {...prev, is_read: !prev.is_read} : null);
    fetchThreads();
  };

  const syncEmails = async () => {
    setSyncing(true);
    try {
      await axios.post(`${API}/email/sync`);
      await fetchThreads();
    } catch (e) { console.error(e); }
    setSyncing(false);
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  // ─── MOBILE THREAD VIEW ───────────────────────────────────────────────────
  if (mobile && mobileView === 'thread' && selected) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', background: '#f9fafb' }}>
        <div style={{ padding: '10px 12px', background: '#fff', borderBottom: '1px solid #e5e7eb', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <button onClick={() => { setMobileView('list'); setSelected(null); localStorage.setItem('assistant_context', 'email'); }}
            style={{ background: 'none', border: '1px solid #d1d5db', borderRadius: 6, padding: '5px 10px', fontSize: 12, cursor: 'pointer', color: '#6b7280', flexShrink: 0 }}>
            ←
          </button>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selected.subject || '(brak tematu)'}</div>
            <div style={{ fontSize: 11, color: '#6b7280' }}>{selected.sender_name || selected.sender_email}</div>
          </div>
          <button onClick={() => handleThread(selected)}
            style={{ fontSize: 11, padding: '5px 10px', borderRadius: 6, border: selected?.is_read ? '1px solid #fde68a' : '1px solid #bbf7d0', background: selected?.is_read ? '#fffbeb' : '#f0fdf4', color: selected?.is_read ? '#92400e' : '#15803d', cursor: 'pointer', flexShrink: 0 }}>
            {selected?.is_read ? '↩ Nowe' : '✓ Zakończ'}
          </button>
          {(currentUser?.role === 'admin' || currentUser?.can_assign) && (
            <button onClick={() => { setShowHistory(h => !h); if (!showHistory) loadHistory(selected.id); }}
              style={{ fontSize: 11, padding: '5px 8px', borderRadius: 6, border: '1px solid #e5e7eb', background: showHistory ? '#f3f4f6' : '#fff', color: '#6b7280', cursor: 'pointer', flexShrink: 0 }}>
              🕐
            </button>
          )}
        </div>
        {showHistory && (currentUser?.role === 'admin' || currentUser?.can_assign) && (
          <div style={{ padding: '6px 12px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb', fontSize: 11 }}>
            {historyLoading && <div style={{ color: '#9ca3af' }}>Ładowanie...</div>}
            {!historyLoading && history.length === 0 && <div style={{ color: '#9ca3af' }}>Brak historii</div>}
            {history.map((h, i) => (
              <div key={h.id} style={{ display: 'flex', gap: 6, paddingBottom: 3, marginBottom: 3, borderBottom: i < history.length-1 ? '1px solid #e5e7eb' : 'none' }}>
                <span>{h.event === 'read' ? '👁' : h.event === 'handled' ? '✅' : h.event === 'assigned' ? '👤' : h.event === 'replied' ? '✉️' : '•'}</span>
                <div>
                  <span style={{ fontWeight: 500, color: '#374151' }}>
                    {h.event === 'read' ? 'Przeczytano' : h.event === 'handled' ? 'Zakończono' : h.event === 'assigned' ? `→ ${h.meta?.assigned_to || '—'}` : h.event === 'replied' ? 'Odpowiedź' : h.event}
                  </span>
                  <span style={{ color: '#9ca3af', marginLeft: 4 }}>{h.actor_name}</span>
                  <div style={{ color: '#9ca3af', fontSize: 10 }}>{h.created_at ? new Date(h.created_at).toLocaleString('pl-PL') : ''}</div>
                </div>
              </div>
            ))}
          </div>
        )}
        <div ref={messagesContainerRef} style={{ flex: 1, overflowY: 'auto', padding: 12 }}>
          {loadingMessages && <div style={{ textAlign: 'center', color: '#9ca3af', padding: 20, fontSize: 13 }}>Ładowanie...</div>}
          {messages.map(m => {
            const isIncoming = !m.is_outgoing;
            return (
              <div key={m.id} style={{ marginBottom: 8, display: 'flex', flexDirection: 'column', alignItems: isIncoming ? 'flex-start' : 'flex-end' }}>
                <div style={{ fontSize: 10, color: '#9ca3af', marginBottom: 2 }}>
                  {m.sender_name || m.sender_email} · {formatDate(m.received_at)}
                </div>
                <div style={{ maxWidth: '85%', padding: '8px 12px', borderRadius: 12, fontSize: 13, lineHeight: 1.5,
                  background: isIncoming ? '#fff' : '#1d4ed8', color: isIncoming ? '#374151' : '#fff',
                  border: isIncoming ? '1px solid #e5e7eb' : 'none' }}>
                  {m.body_html ? (
                    <EmailFrame html={showImages.has(m.id) ? activateImages(m.body_html!) : m.body_html!} />
                  ) : (
                    <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {showOriginalPl[m.id] ? m.body_text_pl : m.body_text}
                    </span>
                  )}
                  {!isIncoming && m.body_text_pl && (
                    <div style={{ marginTop: 2 }}>
                      <button onClick={() => setShowOriginalPl(prev => ({...prev, [m.id]: !prev[m.id]}))}
                        style={{ fontSize: 11, padding: '2px 8px', borderRadius: 6, border: '1px solid #bfdbfe', background: showOriginalPl[m.id] ? '#1d4ed8' : '#eff6ff', color: showOriginalPl[m.id] ? '#fff' : '#1d4ed8', cursor: 'pointer' }}>
                        {showOriginalPl[m.id] ? 'Pokaż przetłumaczone' : 'Pokaż PL'}
                      </button>
                    </div>
                  )}
                  {isIncoming && (
                    <div style={{ marginTop: 4 }}>
                      <button onClick={() => translateMessage(m.id, m.body_text || '')}
                        style={{ fontSize: 11, padding: '2px 8px', borderRadius: 6, border: '1px solid #e5e7eb', background: showTranslation[m.id] ? '#eff6ff' : '#f9fafb', color: showTranslation[m.id] ? '#1d4ed8' : '#6b7280', cursor: 'pointer' }}>
                        {translatingMsg === m.id ? '⏳' : '🌐 PL'}
                      </button>
                      {showTranslation[m.id] && translations[m.id] && (
                        <div style={{ marginTop: 6, padding: '8px 12px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, fontSize: 12, color: '#1e3a8a', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                          <div style={{ fontSize: 10, color: '#3b82f6', marginBottom: 4, fontWeight: 600 }}>🌐 TŁUMACZENIE (PL)</div>
                          {translations[m.id]}
                        </div>
                      )}
                    </div>
                  )}
                  {isIncoming && (m.spf_pass !== null || m.dkim_pass !== null) && (
                    <div style={{ marginTop: 4, display: 'flex', gap: 4 }}>
                      {m.spf_pass !== null && <span style={{ fontSize: 10, padding: '1px 5px', borderRadius: 4,
                        background: m.spf_pass ? '#f0fdf4' : '#fef2f2', color: m.spf_pass ? '#15803d' : '#dc2626' }}>
                        SPF {m.spf_pass ? '✓' : '✗'}
                      </span>}
                      {m.dkim_pass !== null && <span style={{ fontSize: 10, padding: '1px 5px', borderRadius: 4,
                        background: m.dkim_pass ? '#f0fdf4' : '#fef2f2', color: m.dkim_pass ? '#15803d' : '#dc2626' }}>
                        DKIM {m.dkim_pass ? '✓' : '✗'}
                      </span>}
                    </div>
                  )}
                  {m.attachments && m.attachments.length > 0 && (
                    <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {m.attachments.map(att => (
                        <div key={att.id}>
                          {att.mime_type && att.mime_type.startsWith('image/') ? (
                            imageUrls[att.id] ? (
                              <img src={imageUrls[att.id]} alt={att.original_filename}
                                onClick={() => setLightboxUrl(imageUrls[att.id]!)}
                                style={{ maxWidth: 200, maxHeight: 200, borderRadius: 8,
                                  border: '1px solid rgba(0,0,0,0.1)', cursor: 'pointer', display: 'block' }} />
                            ) : (
                              <button onClick={() => loadImageAttachment(att.id)} title='Kliknij aby zobaczyć'
                                style={{ fontSize: 12, color: isIncoming ? '#1d4ed8' : '#fff', background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
                                🖼 {att.original_filename}
                                <span style={{ opacity: 0.6, fontSize: 11 }}>({formatSize(att.size_bytes)})</span>
                              </button>
                            )
                          ) : (
                            <button onClick={() => downloadAttachment(att.id, att.original_filename)}
                              style={{ fontSize: 12, color: isIncoming ? '#1d4ed8' : '#fff', background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
                              {getFileIcon(att.mime_type)} {att.original_filename}
                              <span style={{ opacity: 0.6, fontSize: 11 }}>({formatSize(att.size_bytes)})</span>
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          <div ref={messagesEndRef} />
        </div>
        <div style={{ padding: '10px 12px', background: '#fff', borderTop: '1px solid #e5e7eb' }}>
          {sent && <div style={{ fontSize: 12, color: '#15803d', marginBottom: 6 }}>✓ Email wysłany</div>}
          <div style={{ fontSize: 11, color: '#7B79C9', marginBottom: 6, padding: '4px 8px', background: '#EEEDFE', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span>🌐 Klient otrzyma odpowiedź w języku:</span>
            <select value={threadLang} onChange={e => { setThreadLang(e.target.value); setReplyTranslation(''); }} style={{ fontSize: 11, padding: '2px 6px', border: '1px solid #C4C2F5', borderRadius: 6, color: '#3C3489', background: '#fff', cursor: 'pointer' }}>
              {[{v:'pl',l:'🇵🇱 Polski'},{v:'en',l:'🇬🇧 Angielski'},{v:'de',l:'🇩🇪 Niemiecki'},{v:'cs',l:'🇨🇿 Czeski'},{v:'sk',l:'🇸🇰 Słowacki'},{v:'hu',l:'🇭🇺 Węgierski'},{v:'ro',l:'🇷🇴 Rumuński'},{v:'uk',l:'🇺🇦 Ukraiński'},{v:'ru',l:'🇷🇺 Rosyjski'},{v:'fr',l:'🇫🇷 Francuski'},{v:'it',l:'🇮🇹 Włoski'}].map(o => (
                <option key={o.v} value={o.v}>{o.l}</option>
              ))}
            </select>
          </div>
          {replyTranslation && (
            <div style={{ fontSize: 12, color: '#3C3489', marginBottom: 6, padding: '6px 8px', background: '#EEEDFE', borderRadius: 6, lineHeight: 1.5 }}>
              <div style={{ fontSize: 11, marginBottom: 3 }}>Podgląd {threadLang.toUpperCase()}:</div>
              {replyTranslation}
            </div>
          )}
          <div style={{ marginBottom: 6 }}>
            <label style={{ fontSize: 11, color: '#6b7280', display: 'block', marginBottom: 2 }}>Odbiorca</label>
            <input type="email" value={replyToAddress} onChange={e => setReplyToAddress(e.target.value)}
              placeholder="adres@email.pl"
              style={{ width: '100%', padding: '6px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, fontFamily: 'system-ui', outline: 'none', boxSizing: 'border-box' }} />
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <DictateButton disabled={sending} onText={(t: string) => { const cur = reply || ''; handleReplyChange(cur ? cur + ' ' + t : t, (v: any) => { setReply(v); setReplyIsManual(true); setReplyTranslation(''); }); }} />
          <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
          <textarea ref={replyTextareaRef} value={reply} onChange={e => { handleReplyChange(e.target.value, (v) => { setReply(v); setReplyIsManual(true); setReplyTranslation(''); }); }} onInput={(e:any)=>{const t=e.currentTarget;t.style.height='auto';t.style.height=Math.min(t.scrollHeight,200)+'px';}}
            onKeyDown={e => { if (e.key === 'Escape') setShowReplySuggestions(false); }}
            placeholder="Wpisz odpowiedź po polsku..." rows={3}
            style={{ width: '100%', padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8,
              fontSize: 13, fontFamily: 'system-ui', resize: 'none', outline: 'none', boxSizing: 'border-box' }} />
          {showReplySuggestions && <ShortcutSuggestions suggestions={replySuggestions} onSelect={s => applyReplyShortcut(s, reply, (v) => { setReply(v); setReplyIsManual(true); setReplyTranslation(''); })} onClose={() => setShowReplySuggestions(false)} />}
          </div>
          </div>
          <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
            <button onClick={() => sendReply(false)} disabled={sending || !reply.trim()}
              style={{ flex: 1, padding: '10px', background: reply.trim() ? '#1d4ed8' : '#9ca3af',
                color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, cursor: reply.trim() ? 'pointer' : 'not-allowed', fontWeight: 500 }}>
              {sending ? '⏳ Wysyłanie...' : 'Wyślij'}
            </button>
            <button onClick={() => sendReply(true)} disabled={sending || !reply.trim()}
              style={{ flex: 1, padding: '10px', background: reply.trim() ? '#f0fdf4' : '#f9fafb',
                color: reply.trim() ? '#15803d' : '#9ca3af', border: '1px solid #bbf7d0', borderRadius: 8, fontSize: 13, cursor: reply.trim() ? 'pointer' : 'not-allowed', fontWeight: 500 }}>
              {sending ? '⏳ Wysyłanie...' : 'Wyślij i ucz'}
            </button>
            <button onClick={generateAI} disabled={aiLoading}
              style={{ padding: '10px 12px', background: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0', borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>
              {aiLoading ? '⏳' : '✦ AI'}
            </button>
            {threadLang !== 'pl' && reply.trim() && (
              <button onClick={async () => { setTranslatingReply(true); const res = await axios.post(`${API}/ai/translate-from-polish`, { text: reply, target_lang: threadLang }); setReplyTranslation(res.data.translation); setTranslatingReply(false); }} disabled={translatingReply}
                style={{ padding: '10px 12px', background: '#EEEDFE', color: '#3C3489', border: '1px solid #C4C2F5', borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>
                {translatingReply ? '⏳' : `🌐 ${threadLang.toUpperCase()}`}
              </button>
            )}
            <button onClick={() => { setReply(''); setReplyTranslation(''); setReplyAttachments([]); }}
              style={{ padding: '10px 14px', background: '#f3f4f6', color: '#6b7280', border: 'none', borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>
              Wyczyść
            </button>
            <label style={{ padding: '10px 14px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>
              📎
              <input type="file" multiple style={{ display: 'none' }} onChange={e => { if (e.target.files) setReplyAttachments(prev => [...prev, ...Array.from(e.target.files!)]); e.target.value=''; }} />
            </label>
          </div>
        </div>
        {lightboxUrl && (
          <div onClick={() => setLightboxUrl(null)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 9999,
              display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-out' }}>
            <img src={lightboxUrl} alt="Podgląd"
              style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: 8, boxShadow: '0 8px 40px rgba(0,0,0,0.5)' }}
              onClick={e => e.stopPropagation()} />
            <button onClick={() => setLightboxUrl(null)}
              style={{ position: 'absolute', top: 16, right: 20, background: 'none', border: 'none',
                color: '#fff', fontSize: 32, cursor: 'pointer', lineHeight: 1 }}>✕</button>
          </div>
        )}
      </div>
    );
  }

  // ─── MOBILE LIST VIEW ─────────────────────────────────────────────────────
  if (mobile) {
    return (
      <>
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
        <div style={{ padding: '8px 12px', background: '#fff', borderBottom: '1px solid #e5e7eb', display: 'flex', gap: 6 }}>
          <input value={search} onChange={e => setSearch(e.target.value)}
            onKeyDown={(e: any) => { if (e.key === 'Enter') fetchThreads(0); }}
            placeholder="Szukaj nadawcy, tematu..."
            style={{ flex: 1, padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
          <button onClick={syncEmails} disabled={syncing}
            style={{ padding: '8px 12px', background: '#f3f4f6', color: '#6b7280', border: 'none', borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>
            {syncing ? '⏳' : '↻'}
          </button>
        </div>
        <div style={{ display: 'flex', gap: 6, padding: '8px 12px', background: '#fff', borderBottom: '1px solid #e5e7eb' }}>
          {[{k:'unread',l:'Nowe'},{k:'all',l:'Wszystkie'}].map(f => (
            <button key={f.k} onClick={() => { setFilter(f.k); fetchThreads(0, f.k); setSelectedThreadIds(new Set()); }}
              style={{ fontSize: 12, padding: '5px 12px', borderRadius: 20, border: '1px solid #d1d5db',
                background: filter === f.k ? '#1d4ed8' : 'transparent', color: filter === f.k ? '#fff' : '#6b7280', cursor: 'pointer', flex: 1 }}>
              {f.l}
            </button>
          ))}
          <AssignButton
            selectedIds={Array.from(selectedThreadIds)}
            endpoint="/api/assign/email-threads"
            currentUser={currentUser}
            onAssigned={() => { setSelectedThreadIds(new Set()); fetchThreads(); }}
            size="small"
          />
          {selectedThreadIds.size > 0 && (
            <button onClick={bulkHandle}
              style={{ fontSize: 12, padding: '4px 8px', borderRadius: 20, border: '1px solid #bbf7d0',
                background: '#f0fdf4', color: '#15803d', cursor: 'pointer', whiteSpace: 'nowrap' }}>
              ✓ Zakończ ({selectedThreadIds.size})
            </button>
          )}
          <button onClick={() => { setShowCompose(true); setCompose(prev => ({...prev, account_id: imapAccounts[0]?.id || ''})); localStorage.setItem('assistant_context', 'email:compose'); }}
            style={{ fontSize: 12, padding: '4px 8px', borderRadius: 20, border: '1px solid #bfdbfe',
              background: '#eff6ff', color: '#1d4ed8', cursor: 'pointer', whiteSpace: 'nowrap' }}>
            ✉️
          </button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', background: '#f9fafb' }}>
          {loading && <div style={{ textAlign: 'center', color: '#9ca3af', padding: 20, fontSize: 13 }}>Ładowanie...</div>}
          {threads.map(t => (
            <div key={t.id}
              style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', cursor: 'pointer',
                background: selectedThreadIds.has(t.id) ? '#fef9c3' : !t.is_read ? '#fff' : '#f9fafb',
                borderLeft: !t.is_read ? '3px solid #93c5fd' : '3px solid transparent',
                display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <input type="checkbox" checked={selectedThreadIds.has(t.id)}
                onChange={e => { e.stopPropagation(); const next = new Set(selectedThreadIds); if (e.target.checked) next.add(t.id); else next.delete(t.id); setSelectedThreadIds(next); }}
                onClick={e => e.stopPropagation()}
                style={{ marginTop: 4, flexShrink: 0, width: 16, height: 16, cursor: 'pointer' }} />
              <div style={{ flex: 1, minWidth: 0 }} onClick={() => selectThread(t)}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                <span style={{ fontWeight: !t.is_read ? 600 : 400, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 220 }}>
                  {t.sender_name || t.sender_email}
                </span>
                <span style={{ fontSize: 12, color: '#9ca3af', flexShrink: 0 }}>{formatDate(t.last_message_at)}</span>
              </div>
              <div style={{ fontSize: 12, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 2 }}>
                {t.subject || '(brak tematu)'}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 11, color: !t.is_read ? '#1d4ed8' : '#9ca3af' }}>{!t.is_read ? '● Nowa' : '○ Przeczytana'}</span>
                {t.has_attachments && <span title="Zawiera załączniki" style={{ fontSize: 12 }}>📎</span>}
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  {t.assigned_to && (() => { const agent = (window as any).__agents__?.find((a: any) => a.id === t.assigned_to); return agent ? <div title={agent.name} style={{ width: 8, height: 8, borderRadius: '50%', background: agent.color || '#9ca3af' }} /> : null; })()}
                  <span style={{ fontSize: 11, color: '#9ca3af' }}>{t.account_name}</span>
                  {(currentUser?.role === 'admin' || currentUser?.can_assign) && (
                    <button onClick={e => { e.stopPropagation();
                      const domain = (t.sender_email || '').split('@')[1] || t.sender_email;
                      setRuleForm({name: domain, sender: domain, subject: (t.subject || ''), logic:'AND', account_id: t.account_id || '', assigned_to:''});
                      setRuleModal({sender: t.sender_email, subject: t.subject || '', account_id: t.account_id || ''});
                    }} title="Zawsze przeczytany" style={{ background:'none', border:'none', cursor:'pointer', fontSize:14, color:'#9ca3af', padding:'0 2px' }}>📌</button>
                  )}
                </div>
              </div>
              </div>
            </div>
          ))}
          {!loading && threads.length === 0 && (
            <div style={{ textAlign: 'center', color: '#9ca3af', padding: 40, fontSize: 14 }}>Brak wiadomości</div>
          )}
        </div>
        <div style={{ display: 'flex', background: '#fff', padding: '6px 12px', borderTop: '1px solid #e5e7eb', alignItems: 'center', gap: 6 }}>
          <button onClick={() => fetchThreads(Math.max(0, page-1))} disabled={page === 0}
            style={{ fontSize: 12, padding: '4px 10px', borderRadius: 6, border: '1px solid #d1d5db', background: 'transparent', cursor: page === 0 ? 'not-allowed' : 'pointer', color: '#6b7280' }}>←</button>
          <span style={{ fontSize: 11, color: '#6b7280', flex: 1, textAlign: 'center' }}>{page+1}/{Math.max(1, totalPages)} · {total} wątków</span>
          <button onClick={() => fetchThreads(Math.min(totalPages-1, page+1))} disabled={page >= totalPages-1}
            style={{ fontSize: 12, padding: '4px 10px', borderRadius: 6, border: '1px solid #d1d5db', background: 'transparent', cursor: page >= totalPages-1 ? 'not-allowed' : 'pointer', color: '#6b7280' }}>→</button>
        </div>
      </div>
      {showCompose && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center' }}
          onClick={() => setShowCompose(false)}>
          <div style={{ background:'#fff', borderRadius:12, padding:24, width:520, maxWidth:'95vw', maxHeight:'90vh', overflowY:'auto' }}
            onClick={(e:any) => e.stopPropagation()}>
            <div style={{ fontSize:15, fontWeight:600, marginBottom:16 }}>✉️ Nowa wiadomość email</div>
            <div style={{ display:'grid', gap:10, marginBottom:12 }}>
              <div>
                <label style={{ fontSize:12, color:'#6b7280', display:'block', marginBottom:3 }}>Konto wysyłające</label>
                <select value={compose.account_id} onChange={e => setCompose({...compose, account_id:e.target.value})}
                  style={{ width:'100%', padding:'7px 10px', border:'1px solid #d1d5db', borderRadius:6, fontSize:13 }}>
                  <option value=''>Wybierz konto...</option>
                  {imapAccounts.map((a:any) => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize:12, color:'#6b7280', display:'block', marginBottom:3 }}>Odbiorca (email)</label>
                <input value={compose.to} onChange={e => setCompose({...compose, to:e.target.value})}
                  placeholder="odbiorca@domena.pl"
                  style={{ width:'100%', padding:'7px 10px', border:'1px solid #d1d5db', borderRadius:6, fontSize:13, boxSizing:'border-box' as const }} />
              </div>
              <div>
                <label style={{ fontSize:12, color:'#6b7280', display:'block', marginBottom:3 }}>Temat</label>
                <input value={compose.subject} onChange={e => setCompose({...compose, subject:e.target.value})}
                  placeholder="Temat wiadomości"
                  style={{ width:'100%', padding:'7px 10px', border:'1px solid #d1d5db', borderRadius:6, fontSize:13, boxSizing:'border-box' as const }} />
              </div>
              <div>
                <label style={{ fontSize:12, color:'#6b7280', display:'block', marginBottom:3 }}>Język wiadomości</label>
                <select value={compose.language} onChange={e => { setCompose({...compose, language:e.target.value}); setComposeTranslation(''); }}
                  style={{ width:'100%', padding:'7px 10px', border:'1px solid #d1d5db', borderRadius:6, fontSize:13 }}>
                  {[{v:'pl',l:'Polski'},{v:'en',l:'Angielski'},{v:'de',l:'Niemiecki'},{v:'fr',l:'Francuski'},{v:'cs',l:'Czeski'},{v:'sk',l:'Słowacki'},{v:'ro',l:'Rumuński'},{v:'hu',l:'Węgierski'},{v:'uk',l:'Ukraiński'},{v:'ru',l:'Rosyjski'}].map((o:any) => (
                    <option key={o.v} value={o.v}>{o.l}</option>
                  ))}
                </select>
              </div>
              <label style={{ fontSize:12, color:'#6b7280', display:'block', marginBottom:3 }}>Treść (pisz po polsku)</label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <DictateButton disabled={sending} onText={(t: string) => { const cur = compose.body || ''; handleComposeChange(cur ? cur + ' ' + t : t, (v: any) => { setCompose({...compose, body: v}); setComposeTranslation(''); }); }} />
              <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
                <textarea value={compose.body} onChange={e => { handleComposeChange(e.target.value, (v) => { setCompose({...compose, body: v}); setComposeTranslation(''); }); }}
                  onKeyDown={e => { if (e.key === 'Escape') setShowComposeSuggestions(false); }} rows={6}
                  placeholder="Wpisz treść wiadomości po polsku..."
                  style={{ width:'100%', padding:'7px 10px', border:'1px solid #d1d5db', borderRadius:6, fontSize:13, fontFamily:'system-ui', resize:'vertical' as const, outline:'none', boxSizing:'border-box' as const }} />
                {showComposeSuggestions && <ShortcutSuggestions suggestions={composeSuggestions} onSelect={s => applyComposeShortcut(s, compose.body, (v) => { setCompose({...compose, body: v}); setComposeTranslation(''); })} onClose={() => setShowComposeSuggestions(false)} />}
              </div>
              </div>
              {compose.language !== 'pl' && compose.body.trim() && (
                <div>
                  {composeTranslation && (
                    <div style={{ padding:'8px 10px', background:'#EEEDFE', borderRadius:6, fontSize:13, color:'#3C3489', lineHeight:1.5, marginBottom:6 }}>
                      <div style={{ fontSize:11, marginBottom:3 }}>Podgląd {compose.language.toUpperCase()}:</div>
                      {composeTranslation}
                    </div>
                  )}
                  <button onClick={async () => { setComposeTranslating(true); const res = await axios.post(`${API}/ai/translate-from-polish`, { text: compose.body, target_lang: compose.language }); setComposeTranslation(res.data.translation); setComposeTranslating(false); }}
                    disabled={composeTranslating}
                    style={{ fontSize:12, padding:'4px 10px', borderRadius:6, border:'1px solid #C4C2F5', background:'#EEEDFE', color:'#3C3489', cursor:'pointer' }}>
                    {composeTranslating ? '⏳' : `🌐 Podgląd ${compose.language.toUpperCase()}`}
                  </button>
                </div>
              )}
              {composeAttachments.length > 0 && (
                <div style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
                  {composeAttachments.map((f:any, i:number) => (
                    <div key={i} style={{ fontSize:11, padding:'2px 8px', background:'#eff6ff', border:'1px solid #bfdbfe', borderRadius:12, display:'flex', alignItems:'center', gap:4 }}>
                      📎 {f.name}
                      <span onClick={() => setComposeAttachments((prev:any) => prev.filter((_:any,j:number) => j!==i))}
                        style={{ cursor:'pointer', color:'#dc2626', fontWeight:700 }}>×</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
              <button onClick={sendCompose} disabled={composeSending || !compose.to || !compose.subject || !compose.body || !compose.account_id}
                style={{ flex:1, padding:'9px', background: compose.to && compose.subject && compose.body && compose.account_id ? '#1d4ed8' : '#9ca3af', color:'#fff', border:'none', borderRadius:8, fontSize:13, cursor:'pointer', fontWeight:500 }}>
                {composeSending ? '⏳ Wysyłanie...' : `✉️ Wyślij${compose.language !== 'pl' ? ' (' + compose.language.toUpperCase() + ')' : ''}`}
              </button>
              <button onClick={generateComposeAI} disabled={composeAiLoading}
                style={{ padding:'9px 14px', background:'#f0fdf4', color:'#15803d', border:'1px solid #bbf7d0', borderRadius:8, fontSize:13, cursor:'pointer' }}>
                {composeAiLoading ? '⏳' : '✦ AI'}
              </button>
              <label style={{ padding:'9px 14px', background:'#f9fafb', color:'#374151', border:'1px solid #d1d5db', borderRadius:8, fontSize:13, cursor:'pointer' }}>
                📎
                <input type="file" multiple style={{ display:'none' }} onChange={(e:any) => { if (e.target.files) setComposeAttachments((prev:any) => [...prev, ...Array.from(e.target.files)]); e.target.value=''; }} />
              </label>
              <button onClick={() => { setShowCompose(false); setComposeTranslation(''); setComposeAttachments([]); localStorage.setItem('assistant_context', selected ? 'email:thread' : 'email'); }}
                style={{ padding:'9px 14px', background:'#f3f4f6', color:'#6b7280', border:'none', borderRadius:8, fontSize:13, cursor:'pointer' }}>
                Anuluj
              </button>
            </div>
          </div>
        </div>
      )}
      {ruleModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:9999, display:'flex', alignItems:'flex-end' }}
          onClick={() => setRuleModal(null)}>
          <div style={{ background:'#fff', borderRadius:'12px 12px 0 0', padding:20, width:'100%', maxHeight:'80vh', overflowY:'auto' }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize:15, fontWeight:600, marginBottom:16 }}>📌 Dodaj regułę automatyczną</div>
            <div style={{ display:'grid', gap:10, marginBottom:12 }}>
              <div><label style={{ fontSize:12, color:'#6b7280', display:'block', marginBottom:3 }}>Nazwa reguły</label>
                <input value={ruleForm.name} onChange={e => setRuleForm({...ruleForm, name:e.target.value})} style={{ width:'100%', padding:'8px 10px', border:'1px solid #d1d5db', borderRadius:6, fontSize:13, boxSizing:'border-box' as const }} /></div>
              <div><label style={{ fontSize:12, color:'#6b7280', display:'block', marginBottom:3 }}>Konto email</label>
                <select value={ruleForm.account_id} onChange={e => setRuleForm({...ruleForm, account_id:e.target.value})} style={{ width:'100%', padding:'8px 10px', border:'1px solid #d1d5db', borderRadius:6, fontSize:13 }}>
                  <option value=''>Wszystkie konta</option>
                  {imapAccounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select></div>
              <div><label style={{ fontSize:12, color:'#6b7280', display:'block', marginBottom:3 }}>Nadawca zawiera</label>
                <input value={ruleForm.sender} onChange={e => setRuleForm({...ruleForm, sender:e.target.value})} style={{ width:'100%', padding:'8px 10px', border:'1px solid #d1d5db', borderRadius:6, fontSize:13, boxSizing:'border-box' as const }} /></div>
              <div><label style={{ fontSize:12, color:'#6b7280', display:'block', marginBottom:3 }}>Temat zawiera</label>
                <input value={ruleForm.subject} onChange={e => setRuleForm({...ruleForm, subject:e.target.value})} style={{ width:'100%', padding:'8px 10px', border:'1px solid #d1d5db', borderRadius:6, fontSize:13, boxSizing:'border-box' as const }} /></div>
              <div><label style={{ fontSize:12, color:'#6b7280', display:'block', marginBottom:3 }}>Logika</label>
                <select value={ruleForm.logic} onChange={e => setRuleForm({...ruleForm, logic:e.target.value})} style={{ width:'100%', padding:'8px 10px', border:'1px solid #d1d5db', borderRadius:6, fontSize:13 }}>
                  <option value='OR'>OR — nadawca LUB temat</option>
                  <option value='AND'>AND — nadawca I temat</option>
                </select></div>
              <div><label style={{ fontSize:12, color:'#6b7280', display:'block', marginBottom:3 }}>Przydziel do agenta (opcjonalnie)</label>
                <select value={ruleForm.assigned_to} onChange={e => setRuleForm({...ruleForm, assigned_to:e.target.value})} style={{ width:'100%', padding:'8px 10px', border:'1px solid #d1d5db', borderRadius:6, fontSize:13 }}>
                  <option value=''>Brak przypisania</option>
                  {agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select></div>
            </div>
            <div style={{ display:'flex', gap:8 }}>
              <button disabled={ruleSaving || !ruleForm.name} onClick={async () => {
                setRuleSaving(true);
                const conditions = [];
                if (ruleForm.sender) conditions.push({field:'sender', operator:'contains', value:ruleForm.sender});
                if (ruleForm.subject) conditions.push({field:'subject', operator:'contains', value:ruleForm.subject});
                await axios.post('/api/email/rules', { name: ruleForm.name, account_id: ruleForm.account_id || null, logic: ruleForm.logic, conditions, assigned_to: ruleForm.assigned_to || null });
                setRuleSaving(false); setRuleModal(null); fetchThreads();
              }} style={{ flex:1, padding:'10px', background:'#1d4ed8', color:'#fff', border:'none', borderRadius:6, fontSize:13, cursor:'pointer' }}>
                {ruleSaving ? 'Zapisuję...' : 'Zapisz regułę'}
              </button>
              <button onClick={() => setRuleModal(null)} style={{ padding:'10px 16px', background:'#f3f4f6', color:'#374151', border:'1px solid #d1d5db', borderRadius:6, fontSize:13, cursor:'pointer' }}>Anuluj</button>
            </div>
          </div>
        </div>
      )}
      </>
    );
  }

  // ─── DESKTOP VIEW ─────────────────────────────────────────────────────────
  return (
    <div style={{ display: 'flex', flexDirection: 'column' as const, height: '100%', fontFamily: 'system-ui, sans-serif', fontSize: 14 }}>
      {imapLoaded && imapAccounts.length === 0 && !emailSetupClosed && (
        <MMEmailSetupBanner
          onDone={() => { loadImapAccounts(); }}
          onClose={() => setEmailSetupClosed(true)}
        />
      )}
      {copyToast && (
        <div style={{ position: 'fixed', top: 20, left: '50%', transform: 'translateX(-50%)',
          background: '#1d4ed8', color: '#fff', padding: '8px 18px', borderRadius: 20,
          fontSize: 13, fontWeight: 500, zIndex: 9999, pointerEvents: 'none',
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }}>
          ✓ Skopiowano: {copyToast}
        </div>
      )}
      {!hideAlertBar && <LiveAlertBar currentUser={currentUser} onOpenAiOpen={onOpenAiOpen} onOpenReturns={onOpenReturns} />}
      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
      {lightboxUrl && (
        <div onClick={() => setLightboxUrl(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-out' }}>
          <img src={lightboxUrl} alt="Podgląd"
            style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: 8, boxShadow: '0 8px 40px rgba(0,0,0,0.5)' }}
            onClick={e => e.stopPropagation()} />
          <button onClick={() => setLightboxUrl(null)}
            style={{ position: 'absolute', top: 16, right: 20, background: 'none', border: 'none',
              color: '#fff', fontSize: 32, cursor: 'pointer', lineHeight: 1 }}>✕</button>
        </div>
      )}
      {showCompose && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center' }}
          onClick={() => setShowCompose(false)}>
          <div style={{ background:'#fff', borderRadius:12, padding:24, width:520, maxWidth:'95vw', maxHeight:'90vh', overflowY:'auto' }}
            onClick={(e:any) => e.stopPropagation()}>
            <div style={{ fontSize:15, fontWeight:600, marginBottom:16 }}>✉️ Nowa wiadomość email</div>
            <div style={{ display:'grid', gap:10, marginBottom:12 }}>
              <div>
                <label style={{ fontSize:12, color:'#6b7280', display:'block', marginBottom:3 }}>Konto wysyłające</label>
                <select value={compose.account_id} onChange={e => setCompose({...compose, account_id:e.target.value})}
                  style={{ width:'100%', padding:'7px 10px', border:'1px solid #d1d5db', borderRadius:6, fontSize:13 }}>
                  <option value=''>Wybierz konto...</option>
                  {imapAccounts.map((a:any) => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize:12, color:'#6b7280', display:'block', marginBottom:3 }}>Odbiorca (email)</label>
                <input value={compose.to} onChange={e => setCompose({...compose, to:e.target.value})}
                  placeholder="odbiorca@domena.pl"
                  style={{ width:'100%', padding:'7px 10px', border:'1px solid #d1d5db', borderRadius:6, fontSize:13, boxSizing:'border-box' as const }} />
              </div>
              <div>
                <label style={{ fontSize:12, color:'#6b7280', display:'block', marginBottom:3 }}>Temat</label>
                <input value={compose.subject} onChange={e => setCompose({...compose, subject:e.target.value})}
                  placeholder="Temat wiadomości"
                  style={{ width:'100%', padding:'7px 10px', border:'1px solid #d1d5db', borderRadius:6, fontSize:13, boxSizing:'border-box' as const }} />
              </div>
              <div>
                <label style={{ fontSize:12, color:'#6b7280', display:'block', marginBottom:3 }}>Język wiadomości</label>
                <select value={compose.language} onChange={e => { setCompose({...compose, language:e.target.value}); setComposeTranslation(''); }}
                  style={{ width:'100%', padding:'7px 10px', border:'1px solid #d1d5db', borderRadius:6, fontSize:13 }}>
                  {[{v:'pl',l:'Polski'},{v:'en',l:'Angielski'},{v:'de',l:'Niemiecki'},{v:'fr',l:'Francuski'},{v:'cs',l:'Czeski'},{v:'sk',l:'Słowacki'},{v:'ro',l:'Rumuński'},{v:'hu',l:'Węgierski'},{v:'uk',l:'Ukraiński'},{v:'ru',l:'Rosyjski'}].map((o:any) => (
                    <option key={o.v} value={o.v}>{o.l}</option>
                  ))}
                </select>
              </div>
              <label style={{ fontSize:12, color:'#6b7280', display:'block', marginBottom:3 }}>Treść (pisz po polsku)</label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <DictateButton disabled={sending} onText={(t: string) => { const cur = compose.body || ''; handleComposeChange(cur ? cur + ' ' + t : t, (v: any) => { setCompose({...compose, body: v}); setComposeTranslation(''); }); }} />
              <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
                <textarea value={compose.body} onChange={e => { handleComposeChange(e.target.value, (v) => { setCompose({...compose, body: v}); setComposeTranslation(''); }); }}
                  onKeyDown={e => { if (e.key === 'Escape') setShowComposeSuggestions(false); }} rows={6}
                  placeholder="Wpisz treść wiadomości po polsku..."
                  style={{ width:'100%', padding:'7px 10px', border:'1px solid #d1d5db', borderRadius:6, fontSize:13, fontFamily:'system-ui', resize:'vertical' as const, outline:'none', boxSizing:'border-box' as const }} />
                {showComposeSuggestions && <ShortcutSuggestions suggestions={composeSuggestions} onSelect={s => applyComposeShortcut(s, compose.body, (v) => { setCompose({...compose, body: v}); setComposeTranslation(''); })} onClose={() => setShowComposeSuggestions(false)} />}
              </div>
              </div>
              {compose.language !== 'pl' && compose.body.trim() && (
                <div>
                  {composeTranslation && (
                    <div style={{ padding:'8px 10px', background:'#EEEDFE', borderRadius:6, fontSize:13, color:'#3C3489', lineHeight:1.5, marginBottom:6 }}>
                      <div style={{ fontSize:11, marginBottom:3 }}>Podgląd {compose.language.toUpperCase()}:</div>
                      {composeTranslation}
                    </div>
                  )}
                  <button onClick={async () => { setComposeTranslating(true); const res = await axios.post(`${API}/ai/translate-from-polish`, { text: compose.body, target_lang: compose.language }); setComposeTranslation(res.data.translation); setComposeTranslating(false); }}
                    disabled={composeTranslating}
                    style={{ fontSize:12, padding:'4px 10px', borderRadius:6, border:'1px solid #C4C2F5', background:'#EEEDFE', color:'#3C3489', cursor:'pointer' }}>
                    {composeTranslating ? '⏳' : `🌐 Podgląd ${compose.language.toUpperCase()}`}
                  </button>
                </div>
              )}
              {composeAttachments.length > 0 && (
                <div style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
                  {composeAttachments.map((f:any, i:number) => (
                    <div key={i} style={{ fontSize:11, padding:'2px 8px', background:'#eff6ff', border:'1px solid #bfdbfe', borderRadius:12, display:'flex', alignItems:'center', gap:4 }}>
                      📎 {f.name}
                      <span onClick={() => setComposeAttachments((prev:any) => prev.filter((_:any,j:number) => j!==i))}
                        style={{ cursor:'pointer', color:'#dc2626', fontWeight:700 }}>×</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
              <button onClick={sendCompose} disabled={composeSending || !compose.to || !compose.subject || !compose.body || !compose.account_id}
                style={{ flex:1, padding:'9px', background: compose.to && compose.subject && compose.body && compose.account_id ? '#1d4ed8' : '#9ca3af', color:'#fff', border:'none', borderRadius:8, fontSize:13, cursor:'pointer', fontWeight:500 }}>
                {composeSending ? '⏳ Wysyłanie...' : `✉️ Wyślij${compose.language !== 'pl' ? ' (' + compose.language.toUpperCase() + ')' : ''}`}
              </button>
              <button onClick={generateComposeAI} disabled={composeAiLoading}
                style={{ padding:'9px 14px', background:'#f0fdf4', color:'#15803d', border:'1px solid #bbf7d0', borderRadius:8, fontSize:13, cursor:'pointer' }}>
                {composeAiLoading ? '⏳' : '✦ AI'}
              </button>
              <label style={{ padding:'9px 14px', background:'#f9fafb', color:'#374151', border:'1px solid #d1d5db', borderRadius:8, fontSize:13, cursor:'pointer' }}>
                📎
                <input type="file" multiple style={{ display:'none' }} onChange={(e:any) => { if (e.target.files) setComposeAttachments((prev:any) => [...prev, ...Array.from(e.target.files)]); e.target.value=''; }} />
              </label>
              <button onClick={() => { setShowCompose(false); setComposeTranslation(''); setComposeAttachments([]); localStorage.setItem('assistant_context', selected ? 'email:thread' : 'email'); }}
                style={{ padding:'9px 14px', background:'#f3f4f6', color:'#6b7280', border:'none', borderRadius:8, fontSize:13, cursor:'pointer' }}>
                Anuluj
              </button>
            </div>
          </div>
        </div>
      )}
      {/* SIDEBAR */}
      <div style={{ width: 300, borderRight: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column', background: '#f9fafb' }}>
        {!hideHeader && (
          <div style={{ padding: '12px 14px', borderBottom: '1px solid #e5e7eb' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontWeight: 600, fontSize: 15 }}>📧 Email</span>
              <div style={{ display: 'flex', gap: 4 }}>
                <button onClick={() => { setShowCompose(true); setCompose(prev => ({...prev, account_id: imapAccounts[0]?.id || ''})); localStorage.setItem('assistant_context', 'email:compose'); }} title="Wyślij nowy email"
                  style={{ fontSize: 12, padding: '3px 8px', borderRadius: 6, border: '1px solid #d1d5db', background: 'transparent', cursor: 'pointer', color: '#6b7280' }}>
                  ✉️ Wyślij
                </button>
                <button onClick={syncEmails} disabled={syncing} title="Synchronizuj skrzynkę"
                  style={{ fontSize: 12, padding: '3px 8px', borderRadius: 6, border: '1px solid #d1d5db',
                    background: syncing ? '#f3f4f6' : 'transparent', cursor: syncing ? 'not-allowed' : 'pointer', color: '#6b7280' }}>
                  {syncing ? '⏳' : '↻'} Sync
                </button>
                <button onClick={onBack}
                  style={{ fontSize: 11, padding: '3px 8px', borderRadius: 6, border: '1px solid #d1d5db', background: 'transparent', cursor: 'pointer', color: '#6b7280' }}>
                  ← Wróć
                </button>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
              <input value={search} onChange={e => setSearch(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') fetchThreads(0); }}
                placeholder="Szukaj nadawcy, tematu..."
                style={{ flex: 1, padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, outline: 'none', boxSizing: 'border-box' }} />
              <button onClick={() => fetchThreads(0)}
                style={{ padding: '6px 10px', background: '#1d4ed8', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>
                Szukaj
              </button>
              {search && <button onClick={() => { setSearch(''); fetchThreads(0); }}
                style={{ padding: '6px 8px', background: '#f3f4f6', color: '#6b7280', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>✕</button>}
            </div>
            <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
              <input type="checkbox" title="Zaznacz wszystkie na stronie"
                checked={threads.length > 0 && threads.every(t => selectedThreadIds.has(t.id))}
                onChange={e => {
                  if (e.target.checked) setSelectedThreadIds(new Set([...Array.from(selectedThreadIds), ...threads.map(t => t.id)]));
                  else { const next = new Set(Array.from(selectedThreadIds)); threads.forEach(t => next.delete(t.id)); setSelectedThreadIds(next); }
                }}
                style={{ cursor: 'pointer', flexShrink: 0 }} />
              {[{k:'unread',l:'Nowe',t:'Nieprzeczytane'},{k:'all',l:'Wszystkie',t:'Wszystkie emaile'}].map(f => (
                <button key={f.k} onClick={() => { setFilter(f.k); fetchThreads(0, f.k); }} title={f.t}
                  style={{ fontSize: 11, padding: '3px 10px', borderRadius: 20, border: '1px solid #d1d5db',
                    background: filter === f.k ? '#1d4ed8' : 'transparent',
                    color: filter === f.k ? '#fff' : '#6b7280', cursor: 'pointer', flex: 1 }}>
                  {f.l}
                </button>
              ))}
              <span title="Przydziel agenta">
              <AssignButton
                selectedIds={Array.from(selectedThreadIds)}
                endpoint="/api/assign/email-threads"
                currentUser={currentUser}
                onAssigned={() => { setSelectedThreadIds(new Set()); fetchThreads(); }}
                size="small"
              />
              </span>
              {selectedThreadIds.size > 0 && (
                <button onClick={bulkHandle} title="Zakończ zaznaczone"
                  style={{ fontSize: 11, padding: '3px 8px', borderRadius: 20, border: '1px solid #bbf7d0',
                    background: '#f0fdf4', color: '#15803d', cursor: 'pointer' }}>
                  ✓ ({selectedThreadIds.size})
                </button>
              )}
            </div>
          </div>
        )}
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {loading && <div style={{ padding: 16, color: '#6b7280', fontSize: 12 }}>Ładowanie...</div>}
          {threads.map(t => (
            <div key={t.id}
              style={{ padding: '9px 12px', borderBottom: '1px solid #e5e7eb', cursor: 'pointer',
                background: selectedThreadIds.has(t.id) ? '#fef9c3' : selected?.id === t.id ? '#eff6ff' : !t.is_read ? '#fff' : '#f9fafb',
                borderLeft: selected?.id === t.id ? '3px solid #1d4ed8' : !t.is_read ? '3px solid #93c5fd' : '3px solid transparent',
                display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <input type="checkbox" checked={selectedThreadIds.has(t.id)}
                onChange={e => { e.stopPropagation(); const next = new Set(selectedThreadIds); if (e.target.checked) next.add(t.id); else next.delete(t.id); setSelectedThreadIds(next); }}
                onClick={e => e.stopPropagation()}
                style={{ marginTop: 3, flexShrink: 0, cursor: 'pointer' }} />
              <div style={{ flex: 1, minWidth: 0 }} onClick={() => selectThread(t)}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                <span style={{ fontWeight: !t.is_read ? 600 : 400, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 180 }}>
                  {t.sender_name || t.sender_email}
                </span>
                <span style={{ fontSize: 11, color: '#9ca3af', flexShrink: 0 }}>{formatDate(t.last_message_at)}</span>
              </div>
              <div style={{ fontSize: 11, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 2 }}>
                {t.subject || '(brak tematu)'}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: !t.is_read ? '#1d4ed8' : 'transparent', border: '1px solid #d1d5db', display: 'inline-block' }}></span>
                  <span style={{ fontSize: 11, color: '#6b7280' }}>{!t.is_read ? 'Nowa' : 'Przeczytana'}</span>
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  {t.assigned_to && (() => { const agent = (window as any).__agents__?.find((a: any) => a.id === t.assigned_to); return agent ? <div title={agent.name} style={{ width: 8, height: 8, borderRadius: '50%', background: agent.color || '#9ca3af', flexShrink: 0 }} /> : null; })()}
                  <span style={{ fontSize: 10, color: '#9ca3af' }}>{t.account_name}</span>
                  {(currentUser?.role === 'admin' || currentUser?.can_assign) && (
                    <button onClick={e => { e.stopPropagation();
                      const domain = (t.sender_email || '').split('@')[1] || t.sender_email;
                      setRuleForm({name: domain, sender: domain, subject: (t.subject || ''), logic:'AND', account_id: t.account_id || '', assigned_to:''});
                      setRuleModal({sender: t.sender_email, subject: t.subject || '', account_id: t.account_id || ''});
                    }} title="Zawsze przeczytany" style={{ background:'none', border:'none', cursor:'pointer', fontSize:12, color:'#9ca3af', padding:'0 2px', lineHeight:1 }}>📌</button>
                  )}
                </div>
              </div>
              </div>
            </div>
          ))}
          {!loading && threads.length === 0 && (
            <div style={{ padding: 24, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>Brak wiadomości</div>
          )}
        </div>
        {ruleModal && (
          <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center' }}
            onClick={() => setRuleModal(null)}>
            <div style={{ background:'#fff', borderRadius:12, padding:24, width:480, maxWidth:'95vw' }} onClick={e => e.stopPropagation()}>
              <div style={{ fontSize:15, fontWeight:600, marginBottom:16 }}>📌 Dodaj regułę automatyczną</div>
              <div style={{ display:'grid', gap:10, marginBottom:12 }}>
                <div><label style={{ fontSize:12, color:'#6b7280', display:'block', marginBottom:3 }}>Nazwa reguły</label>
                  <input value={ruleForm.name} onChange={e => setRuleForm({...ruleForm, name:e.target.value})} style={{ width:'100%', padding:'6px 10px', border:'1px solid #d1d5db', borderRadius:6, fontSize:13, boxSizing:'border-box' as const }} /></div>
                <div><label style={{ fontSize:12, color:'#6b7280', display:'block', marginBottom:3 }}>Konto email</label>
                  <select value={ruleForm.account_id} onChange={e => setRuleForm({...ruleForm, account_id:e.target.value})} style={{ width:'100%', padding:'6px 10px', border:'1px solid #d1d5db', borderRadius:6, fontSize:13 }}>
                    <option value=''>Wszystkie konta</option>
                    {imapAccounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select></div>
                <div><label style={{ fontSize:12, color:'#6b7280', display:'block', marginBottom:3 }}>Nadawca zawiera</label>
                  <input value={ruleForm.sender} onChange={e => setRuleForm({...ruleForm, sender:e.target.value})} style={{ width:'100%', padding:'6px 10px', border:'1px solid #d1d5db', borderRadius:6, fontSize:13, boxSizing:'border-box' as const }} /></div>
                <div><label style={{ fontSize:12, color:'#6b7280', display:'block', marginBottom:3 }}>Temat zawiera</label>
                  <input value={ruleForm.subject} onChange={e => setRuleForm({...ruleForm, subject:e.target.value})} style={{ width:'100%', padding:'6px 10px', border:'1px solid #d1d5db', borderRadius:6, fontSize:13, boxSizing:'border-box' as const }} /></div>
                <div><label style={{ fontSize:12, color:'#6b7280', display:'block', marginBottom:3 }}>Logika</label>
                  <select value={ruleForm.logic} onChange={e => setRuleForm({...ruleForm, logic:e.target.value})} style={{ width:'100%', padding:'6px 10px', border:'1px solid #d1d5db', borderRadius:6, fontSize:13 }}>
                    <option value='OR'>OR — nadawca LUB temat</option>
                    <option value='AND'>AND — nadawca I temat</option>
                  </select></div>
                <div><label style={{ fontSize:12, color:'#6b7280', display:'block', marginBottom:3 }}>Przydziel do agenta (opcjonalnie)</label>
                  <select value={ruleForm.assigned_to} onChange={e => setRuleForm({...ruleForm, assigned_to:e.target.value})} style={{ width:'100%', padding:'6px 10px', border:'1px solid #d1d5db', borderRadius:6, fontSize:13 }}>
                    <option value=''>Brak przypisania</option>
                    {agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select></div>
              </div>
              <div style={{ display:'flex', gap:8 }}>
                <button disabled={ruleSaving || !ruleForm.name} onClick={async () => {
                  setRuleSaving(true);
                  const conditions = [];
                  if (ruleForm.sender) conditions.push({field:'sender', operator:'contains', value:ruleForm.sender});
                  if (ruleForm.subject) conditions.push({field:'subject', operator:'contains', value:ruleForm.subject});
                  await axios.post('/api/email/rules', {
                    name: ruleForm.name,
                    account_id: ruleForm.account_id || null,
                    logic: ruleForm.logic,
                    conditions,
                    assigned_to: ruleForm.assigned_to || null
                  });
                  setRuleSaving(false);
                  setRuleModal(null);
                  fetchThreads();
                }} style={{ padding:'7px 16px', background:'#1d4ed8', color:'#fff', border:'none', borderRadius:6, fontSize:13, cursor:'pointer' }}>
                  {ruleSaving ? 'Zapisuję...' : 'Zapisz regułę'}
                </button>
                <button onClick={() => setRuleModal(null)} style={{ padding:'7px 16px', background:'#f3f4f6', color:'#374151', border:'1px solid #d1d5db', borderRadius:6, fontSize:13, cursor:'pointer' }}>Anuluj</button>
              </div>
            </div>
          </div>
        )}
        <div style={{ padding: '8px 12px', borderTop: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: 6 }}>
          <button onClick={() => fetchThreads(Math.max(0, page-1))} disabled={page === 0}
            style={{ fontSize: 12, padding: '2px 8px', borderRadius: 6, border: '1px solid #d1d5db', background: 'transparent', cursor: page === 0 ? 'not-allowed' : 'pointer', color: '#6b7280' }}>←</button>
          <span style={{ fontSize: 11, color: '#6b7280', flex: 1, textAlign: 'center' }}>{page+1}/{Math.max(1, totalPages)} · {total} wątków</span>
          <button onClick={() => fetchThreads(Math.min(totalPages-1, page+1))} disabled={page >= totalPages-1}
            style={{ fontSize: 12, padding: '2px 8px', borderRadius: 6, border: '1px solid #d1d5db', background: 'transparent', cursor: page >= totalPages-1 ? 'not-allowed' : 'pointer', color: '#6b7280' }}>→</button>
        </div>
      </div>

      {/* MAIN */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {!selected ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>📧</div>
              <div style={{ fontSize: 16, fontWeight: 500 }}>Wybierz wiadomość</div>
              <div style={{ fontSize: 13, marginTop: 4 }}>Skrzynka email klientów</div>
            </div>
          </div>
        ) : (
          <>
            <div style={{ padding: '12px 18px', borderBottom: '1px solid #e5e7eb', background: '#fff' }}>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>{selected.subject || '(brak tematu)'}</div>
              <div style={{ display: 'flex', gap: 6, fontSize: 12, color: '#6b7280', flexWrap: 'wrap', alignItems: 'center' }}>
                <span>{selected.sender_name || selected.sender_email}</span>
                <span title="Kliknij aby skopiować" onClick={() => { navigator.clipboard.writeText(selected.sender_email); setCopyToast(selected.sender_email); setTimeout(() => setCopyToast(''), 1500); }}
                  style={{ color: '#9ca3af', cursor: 'copy' }}>&lt;{selected.sender_email}&gt;</span>
                <span style={{ padding: '1px 8px', borderRadius: 10, fontSize: 11, background: '#f0fdf4', color: '#15803d' }}>{selected.account_name}</span>
                <span style={{ fontSize: 11, color: '#9ca3af' }}>{formatDateTime(selected.last_message_at)}</span>
                <button onClick={() => handleThread(selected)}
                  style={{ fontSize: 11, padding: '1px 10px', borderRadius: 10, border: selected?.is_read ? '1px solid #fde68a' : '1px solid #bbf7d0', background: selected?.is_read ? '#fffbeb' : '#f0fdf4', color: selected?.is_read ? '#92400e' : '#15803d', cursor: 'pointer' }}>
                  {selected?.is_read ? '↩ Nowe' : '✓ Zakończ'}
                </button>
                {(currentUser?.role === 'admin' || currentUser?.can_assign) && (
                  <button onClick={() => { setShowHistory(h => !h); if (!showHistory) loadHistory(selected.id); }}
                    style={{ fontSize: 11, padding: '1px 10px', borderRadius: 10, border: '1px solid #e5e7eb', background: showHistory ? '#f3f4f6' : '#fff', color: '#6b7280', cursor: 'pointer' }}>
                    🕐 Historia
                  </button>
                )}
              </div>
              {showHistory && (currentUser?.role === 'admin' || currentUser?.can_assign) && (
                <div style={{ marginTop: 8, padding: '8px 12px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 12 }}>
                  {historyLoading && <div style={{ color: '#9ca3af' }}>Ładowanie...</div>}
                  {!historyLoading && history.length === 0 && <div style={{ color: '#9ca3af' }}>Brak historii</div>}
                  {history.map((h, i) => (
                    <div key={h.id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', paddingBottom: 6, marginBottom: 6, borderBottom: i < history.length-1 ? '1px solid #e5e7eb' : 'none' }}>
                      <span style={{ fontSize: 14 }}>
                        {h.event === 'read' ? '👁' : h.event === 'handled' ? '✅' : h.event === 'assigned' ? '👤' : h.event === 'replied' ? '✉️' : '•'}
                      </span>
                      <div>
                        <span style={{ fontWeight: 500, color: '#374151' }}>
                          {h.event === 'read' ? 'Oznaczono jako przeczytane' : h.event === 'handled' ? 'Zakończono' : h.event === 'assigned' ? `Przydzielono do: ${h.meta?.assigned_to || '—'}` : h.event === 'replied' ? 'Wysłano odpowiedź' : h.event}
                        </span>
                        <span style={{ color: '#9ca3af', marginLeft: 6 }}>{h.actor_name}</span>
                        <div style={{ color: '#9ca3af', fontSize: 11 }}>{h.created_at ? new Date(h.created_at).toLocaleString('pl-PL') : ''}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div style={{ flex: 1, padding: '14px 18px', overflowY: 'auto', background: '#f9fafb' }}>
              {loadingMessages && <div style={{ color: '#6b7280', fontSize: 12, textAlign: 'center', padding: 20 }}>Ładowanie wiadomości...</div>}
              {messages.map(m => {
                const isIncoming = !m.is_outgoing;
                return (
                  <div key={m.id} style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 3 }}>
                      {m.sender_name || m.sender_email} · {formatDateTime(m.received_at)}
                    </div>
                    {m.body_html ? (
                      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
                        <EmailFrame html={showImages.has(m.id) ? activateImages(m.body_html!) : m.body_html!} />
                      </div>
                    ) : m.body_text ? (
                      <div style={{ display: 'flex', justifyContent: isIncoming ? 'flex-start' : 'flex-end' }}>
                        <div style={{ maxWidth: '72%', padding: '9px 13px', borderRadius: 12, fontSize: 13, lineHeight: 1.6,
                          background: isIncoming ? '#fff' : '#1d4ed8', color: isIncoming ? '#374151' : '#fff',
                          border: isIncoming ? '1px solid #e5e7eb' : 'none' }}>
                          <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {showOriginalPl[m.id] ? m.body_text_pl : m.body_text}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div style={{ color: '#9ca3af', fontSize: 12, fontStyle: 'italic', padding: '8px 0' }}>
                        Treść wiadomości niedostępna
                      </div>
                    )}
                    {!isIncoming && m.body_text_pl && (
                      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 2 }}>
                        <button onClick={() => setShowOriginalPl(prev => ({...prev, [m.id]: !prev[m.id]}))}
                          style={{ fontSize: 11, padding: '2px 8px', borderRadius: 6, border: '1px solid #bfdbfe', background: showOriginalPl[m.id] ? '#1d4ed8' : '#eff6ff', color: showOriginalPl[m.id] ? '#fff' : '#1d4ed8', cursor: 'pointer' }}>
                          {showOriginalPl[m.id] ? 'Pokaż przetłumaczone' : 'Pokaż PL'}
                        </button>
                      </div>
                    )}
                    {isIncoming && (
                      <div style={{ marginTop: 4 }}>
                        <button onClick={() => translateMessage(m.id, m.body_text || '')}
                          style={{ fontSize: 11, padding: '2px 8px', borderRadius: 6, border: '1px solid #e5e7eb', background: showTranslation[m.id] ? '#eff6ff' : '#f9fafb', color: showTranslation[m.id] ? '#1d4ed8' : '#6b7280', cursor: 'pointer' }}>
                          {translatingMsg === m.id ? '⏳' : '🌐 PL'}
                        </button>
                        {showTranslation[m.id] && translations[m.id] && (
                          <div style={{ marginTop: 6, padding: '8px 12px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, fontSize: 13, color: '#1e3a8a', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                            <div style={{ fontSize: 10, color: '#3b82f6', marginBottom: 4, fontWeight: 600 }}>🌐 TŁUMACZENIE (PL)</div>
                            {translations[m.id]}
                          </div>
                        )}
                      </div>
                    )}
                    {m.attachments && m.attachments.length > 0 && (
                      <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {m.attachments.map((att: any) => (
                          <div key={att.id}>
                            {att.mime_type && att.mime_type.startsWith('image/') ? (
                              imageUrls[att.id] ? (
                                <img src={imageUrls[att.id]} alt={att.original_filename}
                                  onClick={() => setLightboxUrl(imageUrls[att.id]!)}
                                  style={{ maxWidth: 200, maxHeight: 200, borderRadius: 8,
                                    border: '1px solid rgba(0,0,0,0.1)', cursor: 'pointer', display: 'block' }} />
                              ) : (
                                <button onClick={() => loadImageAttachment(att.id)} title='Kliknij aby zobaczyć'
                                  style={{ fontSize: 12, color: '#1d4ed8', background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
                                  🖼 {att.original_filename}
                                  <span style={{ opacity: 0.6, fontSize: 11 }}>({formatSize(att.size_bytes)})</span>
                                </button>
                              )
                            ) : (
                              <button onClick={() => downloadAttachment(att.id, att.original_filename)}
                                style={{ fontSize: 12, color: '#1d4ed8', background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
                                {getFileIcon(att.mime_type)} {att.original_filename}
                                <span style={{ opacity: 0.6, fontSize: 11 }}>({formatSize(att.size_bytes)})</span>
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
            <div style={{ borderTop: '1px solid #e5e7eb', background: '#fff', padding: '12px 18px' }}>
              {sent && <div style={{ padding: '7px 12px', background: '#f0fdf4', color: '#15803d', borderRadius: 6, fontSize: 12, marginBottom: 8 }}>✓ Email wysłany</div>}
              <div style={{ fontSize: 11, color: '#7B79C9', marginBottom: 6, padding: '4px 8px', background: '#EEEDFE', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <span>🌐 Klient otrzyma odpowiedź w języku:</span>
                <select value={threadLang} onChange={e => { setThreadLang(e.target.value); setReplyTranslation(''); }} style={{ fontSize: 11, padding: '2px 6px', border: '1px solid #C4C2F5', borderRadius: 6, color: '#3C3489', background: '#fff', cursor: 'pointer' }}>
                  {[{v:'pl',l:'🇵🇱 Polski'},{v:'en',l:'🇬🇧 Angielski'},{v:'de',l:'🇩🇪 Niemiecki'},{v:'cs',l:'🇨🇿 Czeski'},{v:'sk',l:'🇸🇰 Słowacki'},{v:'hu',l:'🇭🇺 Węgierski'},{v:'ro',l:'🇷🇴 Rumuński'},{v:'uk',l:'🇺🇦 Ukraiński'},{v:'ru',l:'🇷🇺 Rosyjski'},{v:'fr',l:'🇫🇷 Francuski'},{v:'it',l:'🇮🇹 Włoski'}].map(o => (
                    <option key={o.v} value={o.v}>{o.l}</option>
                  ))}
                </select>
              </div>
              {replyTranslation && (
                <div style={{ fontSize: 13, color: '#3C3489', marginBottom: 8, padding: '8px 10px', background: '#EEEDFE', borderRadius: 6, lineHeight: 1.5 }}>
                  <div style={{ fontSize: 11, marginBottom: 3 }}>Podgląd {threadLang.toUpperCase()}:</div>
                  {replyTranslation}
                </div>
              )}
              <div style={{ marginBottom: 8 }}>
                <label style={{ fontSize: 12, color: '#6b7280', display: 'block', marginBottom: 3 }}>Odbiorca</label>
                <input type="email" value={replyToAddress} onChange={e => setReplyToAddress(e.target.value)}
                  placeholder="adres@email.pl"
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, fontFamily: 'system-ui', outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <DictateButton disabled={sending} onText={(t: string) => { const cur = reply || ''; handleReplyChange(cur ? cur + ' ' + t : t, (v: any) => { setReply(v); setReplyIsManual(true); setReplyTranslation(''); }); }} />
              <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
              <textarea ref={replyTextareaRef} value={reply} onChange={e => { handleReplyChange(e.target.value, (v) => { setReply(v); setReplyIsManual(true); setReplyTranslation(''); }); }} onInput={(e:any)=>{const t=e.currentTarget;t.style.height='auto';t.style.height=Math.min(t.scrollHeight,200)+'px';}}
                onKeyDown={e => { if (e.key === 'Escape') setShowReplySuggestions(false); }}
                placeholder="Wpisz odpowiedź po polsku..."
                style={{ width: '100%', minHeight: 120, padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, fontFamily: 'system-ui', resize: 'vertical', outline: 'none', boxSizing: 'border-box' }} />
              {showReplySuggestions && <ShortcutSuggestions suggestions={replySuggestions} onSelect={s => applyReplyShortcut(s, reply, (v) => { setReply(v); setReplyIsManual(true); setReplyTranslation(''); })} onClose={() => setShowReplySuggestions(false)} />}
              </div>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                <button onClick={() => sendReply(false)} disabled={sending || !reply.trim()}
                  style={{ padding: '7px 16px', background: !reply.trim() ? '#9ca3af' : '#1d4ed8', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, cursor: !reply.trim() ? 'not-allowed' : 'pointer', fontWeight: 500 }}>
                  {sending ? '⏳ Wysyłanie...' : 'Wyślij'}
                </button>
                <button onClick={() => sendReply(true)} disabled={sending || !reply.trim()}
                  style={{ padding: '7px 16px', background: !reply.trim() ? '#f9fafb' : '#f0fdf4', color: !reply.trim() ? '#9ca3af' : '#15803d', border: '1px solid #bbf7d0', borderRadius: 6, fontSize: 13, cursor: !reply.trim() ? 'not-allowed' : 'pointer', fontWeight: 500 }}>
                  {sending ? '⏳ Wysyłanie...' : 'Wyślij i ucz'}
                </button>
                <button onClick={generateAI} disabled={aiLoading}
                  style={{ padding: '7px 14px', background: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>
                  {aiLoading ? '⏳' : '✦ AI'}
                </button>
                {threadLang !== 'pl' && reply.trim() && (
                  <button onClick={async () => { setTranslatingReply(true); const res = await axios.post(`${API}/ai/translate-from-polish`, { text: reply, target_lang: threadLang }); setReplyTranslation(res.data.translation); setTranslatingReply(false); }} disabled={translatingReply}
                    style={{ padding: '7px 14px', background: '#EEEDFE', color: '#3C3489', border: '1px solid #C4C2F5', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>
                    {translatingReply ? '⏳' : `🌐 ${threadLang.toUpperCase()}`}
                  </button>
                )}
                <button onClick={() => { setReply(''); setReplyTranslation(''); setReplyAttachments([]); }}
                  style={{ padding: '7px 14px', background: 'transparent', color: '#6b7280', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>
                  Wyczyść
                </button>
                <label style={{ padding: '7px 14px', background: '#f9fafb', color: '#374151', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>
                  📎 Załącz
                  <input type="file" multiple style={{ display: 'none' }} onChange={e => { if (e.target.files) setReplyAttachments(prev => [...prev, ...Array.from(e.target.files!)]); e.target.value=''; }} />
                </label>
                {messages.some(m => (m.body_html || '').includes('data-src=')) && (
                  <button onClick={() => setShowImages(prev => { const s = new Set(prev); messages.forEach(m => s.add(m.id)); return s; })}
                    style={{ padding: '7px 14px', background: '#eff6ff', color: '#1d4ed8', border: '1px solid #93c5fd', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>
                    🖼 Pokaż obrazki
                  </button>
                )}
              {replyAttachments.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
                  {replyAttachments.map((f, i) => (
                    <div key={i} style={{ fontSize: 11, padding: '2px 8px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
                      📎 {f.name}
                      <span onClick={() => setReplyAttachments(prev => prev.filter((_, j) => j !== i))}
                        style={{ cursor: 'pointer', color: '#dc2626', fontWeight: 700, marginLeft: 2 }}>×</span>
                    </div>
                  ))}
                </div>
              )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
    </div>
  );
}
